// Sidecar persistence (§8): load on open, write 1 s after the last change of a photo (debounced per
// photo), only through the folder's guarded writer. A sidecar whose source size/lastModified do not
// match is ignored and the photo gets a warning badge.
import { useLibrary, toLibraryPhotos, type LibraryPhoto } from './library';
import { useEditor } from './editor';
import { defaultSettings } from '../model/defaults';
import { makeSidecar, parseSidecar, sidecarMatches } from '../model/sidecar';
import type { PhotoFolder } from '../io/fs';
import type { Settings } from '../model/types';

export const SIDECAR_DEBOUNCE_MS = 1000;

const isDefault = (s: Settings) => JSON.stringify(s) === JSON.stringify(defaultSettings());

/** Opens a folder: lists photos, reads their sidecars, restores settings/ratings, starts autosave. */
export async function openFolder(folder: PhotoFolder): Promise<LibraryPhoto[]> {
  // Save what is pending for the current folder, then stop watching it: restoring the new folder's
  // settings below must not schedule saves (CODE_REVIEW S2, S3).
  await flushSidecars();
  stopAutosave();
  const files = await folder.list();
  const photos = toLibraryPhotos(files);
  const editor = useEditor.getState();
  // Read sidecars with limited concurrency (2,000 photos must not open 2,000 files at once).
  let next = 0;
  const work = async () => {
    while (next < photos.length) {
      const p = photos[next++]!;
      const text = await folder.readSidecar(p);
      if (!text) continue;
      try {
        const sc = parseSidecar(text);
        if (!sidecarMatches(sc, p)) {
          p.sidecarMismatch = true;
          continue;
        }
        p.hasSidecar = true;
        p.rating = Math.min(5, Math.max(0, sc.rating)) as LibraryPhoto['rating'];
        p.rejected = sc.rejected;
        p.edited = !isDefault(sc.settings);
        editor.replace(p.id, sc.settings);
      } catch {
        // Refused (newer schema, another app, broken JSON): keep the file exactly as it is.
        p.sidecarLocked = true;
      }
    }
  };
  await Promise.all(Array.from({ length: 8 }, work));
  useLibrary.getState().setFolder(folder, photos);
  startAutosave();
  return photos;
}

// ── Autosave ─────────────────────────────────────────────────────────
const timers = new Map<string, number>();
let unsubs: (() => void)[] = [];
let lastSettings: Record<string, Settings> = {};
let lastPhotos = new Map<string, { rating: number; rejected: boolean }>();

function schedule(id: string) {
  clearTimeout(timers.get(id));
  timers.set(
    id,
    window.setTimeout(() => {
      timers.delete(id);
      void saveNow(id);
    }, SIDECAR_DEBOUNCE_MS),
  );
}

export async function saveNow(id: string) {
  const { folder, photos } = useLibrary.getState();
  const p = photos.find((x) => x.id === id);
  if (!folder || !p || p.sidecarLocked) return;
  const settings = useEditor.getState().get(id);
  const sc = makeSidecar(
    { name: p.name, size: p.size, lastModified: p.lastModified },
    settings,
    p.rating,
    p.rejected,
  );
  await folder.writeSidecar(p, JSON.stringify(sc, null, 1));
  useLibrary.getState().patch(id, { hasSidecar: true, sidecarMismatch: false, edited: !isDefault(settings) });
}

/** Writes every pending sidecar immediately (before unload / switching folders / tests). */
export async function flushSidecars() {
  const ids = [...timers.keys()];
  for (const id of ids) clearTimeout(timers.get(id));
  timers.clear();
  await Promise.all(ids.map(saveNow));
}

export function stopAutosave() {
  for (const u of unsubs) u();
  unsubs = [];
  for (const t of timers.values()) clearTimeout(t);
  timers.clear();
}

export function startAutosave() {
  for (const u of unsubs) u();
  lastSettings = { ...useEditor.getState().settings };
  lastPhotos = new Map(
    useLibrary.getState().photos.map((p) => [p.id, { rating: p.rating, rejected: p.rejected }]),
  );
  unsubs = [
    useEditor.subscribe((st) => {
      for (const [id, s] of Object.entries(st.settings)) {
        if (lastSettings[id] !== s) schedule(id);
      }
      lastSettings = { ...st.settings };
    }),
    useLibrary.subscribe((st) => {
      for (const p of st.photos) {
        const prev = lastPhotos.get(p.id);
        if (prev && (prev.rating !== p.rating || prev.rejected !== p.rejected)) schedule(p.id);
      }
      lastPhotos = new Map(st.photos.map((p) => [p.id, { rating: p.rating, rejected: p.rejected }]));
    }),
  ];
}

// beforeunload alone does not wait for the async write; pagehide and a hidden tab come earlier (S4).
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => void flushSidecars());
  window.addEventListener('pagehide', () => void flushSidecars());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushSidecars();
  });
}
